import { ChangeDetectionStrategy, Component, effect, input, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { form } from '@angular/forms/signals';
import { NgStyle } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

type SelectableTag = {
    id: number;
    name: string;
    colorText?: string;
    colorBackground?: string;
};

/**
 * Above this perceived brightness (ITU-R BT.601, 0..1) a background counts as light, so the derived
 * text colour has to go darker. HSL lightness alone misjudges it — pure green sits at 50% lightness
 * but reads almost white.
 */
const LIGHT_BACKGROUND_BRIGHTNESS = 0.5;

/** Saturation multiplier making the derived text colour read as a deeper shade of the background hue. */
const TEXT_SATURATION_RATIO = 1.35;
const TEXT_SATURATION_MAX = 90;

/** Lightness bounds of the derived text colour: dark shade on light backgrounds, light shade on dark ones. */
const TEXT_LIGHTNESS_RATIO = 0.35;
const TEXT_LIGHTNESS_DARK_RANGE: [number, number] = [20, 38];
const TEXT_LIGHTNESS_LIGHT_OFFSET = 55;
const TEXT_LIGHTNESS_LIGHT_RANGE: [number, number] = [78, 92];

@Component({
    selector: 'web-tags-collection',
    templateUrl: './web-tags-collection.component.html',
    styleUrls: ['./web-tags-collection.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgStyle, FormsModule, TranslateModule, NgSelectModule, TooltipModule],
})
export class WebTagsCollectionComponent {
    public readonly tags = input.required<SelectableTag[]>();
    public readonly isLoading = input<boolean>(false);
    public readonly isError = input<boolean>(false);

    /** Currently selected tag ids. */
    public readonly selected = input.required<number[]>();
    public readonly selectedChange = output<number[]>();

    /**
     * Internal signal form holding the selection. ng-select is a CVA control
     * (no `[formField]` support), so ngModel reads the form value and writes
     * back through `onTagIdsChange`.
     */
    private readonly model = signal<number[]>([]);
    protected readonly tagsForm = form(this.model);

    constructor() {
        // input → form (host value, undo/redo, external resets). Copy the array
        // into a fresh, mutable one: the host value may be a frozen NgRx store
        // reference, and `form(this.model)` attaches its own tracking metadata to
        // the value — writing to a frozen object throws "object is not extensible"
        // (matches web-crm-pipelines-collection's defensive clone of its input).
        effect(() => {
            this.model.set([...(this.selected() ?? [])]);
        });

        // form → output: emits only when the selection *set* diverges from the
        // input, i.e. on user edits — the input → form sync above converges
        // them again, so no echo loop. Order is irrelevant: ng-select emits ids
        // in interaction order, so an index-by-index diff would emit a spurious
        // change for a reordered-but-equal set.
        effect(() => {
            const ids = this.tagsForm().value();
            const current = untracked(this.selected) ?? [];

            if (!this.isSameTagSet(ids, current)) {
                this.selectedChange.emit(ids);
            }
        });
    }

    /** Derived text colours are cached per background: `getTagStyles` runs on every change detection pass. */
    private readonly derivedTextColors = new Map<string, string | null>();

    public getTagStyles(tag: SelectableTag): Record<string, string> {
        const color = tag.colorText || this.getDerivedTextColor(tag.colorBackground);

        return {
            ...(color && { color }),
            ...(tag.colorBackground && { 'background-color': tag.colorBackground }),
        };
    }

    public onTagIdsChange(tagIds: number[]): void {
        this.tagsForm().value.set((tagIds ?? []).filter((tagId): tagId is number => typeof tagId === 'number'));
    }

    /**
     * CRM tags may arrive without an explicit text colour. Derive one from the background: same hue,
     * more saturated and shifted in lightness, so the label stays legible while visually belonging to
     * the pill. Returns null when the background is missing or unparsable — the pill then inherits.
     */
    private getDerivedTextColor(background?: string): string | null {
        if (!background) {
            return null;
        }

        if (this.derivedTextColors.has(background)) {
            return this.derivedTextColors.get(background) ?? null;
        }

        const rgb = this.parseColor(background);
        const color = rgb && this.toDerivedTextColor(rgb);

        this.derivedTextColors.set(background, color);

        return color;
    }

    private toDerivedTextColor(rgb: [number, number, number]): string {
        const [hue, saturation, lightness] = this.rgbToHsl(rgb);
        // A greyscale background stays greyscale — boosting saturation of an undefined hue tints it randomly.
        const nextSaturation = saturation === 0 ? 0 : Math.min(saturation * TEXT_SATURATION_RATIO, TEXT_SATURATION_MAX);
        const nextLightness =
            this.getBrightness(rgb) >= LIGHT_BACKGROUND_BRIGHTNESS
                ? this.clamp(lightness * TEXT_LIGHTNESS_RATIO, TEXT_LIGHTNESS_DARK_RANGE)
                : this.clamp(lightness + TEXT_LIGHTNESS_LIGHT_OFFSET, TEXT_LIGHTNESS_LIGHT_RANGE);

        return `hsl(${Math.round(hue)}, ${Math.round(nextSaturation)}%, ${Math.round(nextLightness)}%)`;
    }

    /** Perceived brightness in 0..1 (ITU-R BT.601 luma coefficients). */
    private getBrightness([red, green, blue]: [number, number, number]): number {
        return (0.299 * red + 0.587 * green + 0.114 * blue) / 255;
    }

    /** Supports the formats the CRM returns: `#rgb`, `#rrggbb`, `rgb()` and `rgba()`. */
    private parseColor(color: string): [number, number, number] | null {
        const hex = color.trim().replace(/^#/, '');

        if (/^[\da-f]{3}$/i.test(hex)) {
            return [0, 1, 2].map((index) => parseInt(hex[index].repeat(2), 16)) as [number, number, number];
        }

        if (/^[\da-f]{6}$/i.test(hex)) {
            return [0, 2, 4].map((index) => parseInt(hex.slice(index, index + 2), 16)) as [number, number, number];
        }

        const rgb = color.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i);

        return rgb ? [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])] : null;
    }

    private rgbToHsl([red, green, blue]: [number, number, number]): [number, number, number] {
        const [r, g, b] = [red, green, blue].map((channel) => channel / 255);
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const delta = max - min;
        const lightness = (max + min) / 2;

        if (delta === 0) {
            return [0, 0, lightness * 100];
        }

        const saturation = delta / (1 - Math.abs(2 * lightness - 1));
        let hue: number;

        if (max === r) {
            hue = ((g - b) / delta) % 6;
        } else if (max === g) {
            hue = (b - r) / delta + 2;
        } else {
            hue = (r - g) / delta + 4;
        }

        return [(((hue * 60) % 360) + 360) % 360, saturation * 100, lightness * 100];
    }

    private clamp(value: number, [min, max]: [number, number]): number {
        return Math.min(Math.max(value, min), max);
    }

    /** Order-insensitive equality of two id collections (duplicates assumed absent). */
    private isSameTagSet(a: number[], b: number[]): boolean {
        if (a.length !== b.length) {
            return false;
        }

        const set = new Set(b);

        return a.every((id) => set.has(id));
    }
}
